import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FipeCombobox } from "@/components/FipeCombobox";
import { CAMBIOS, COMBUSTIVEIS, CORES, ESTADOS, STATIC_BRANDS, OPCIONAIS, CONDICOES, parseBRL, formatBRL } from "@/lib/carOptions";
import {
  Car, X, Loader2, ImagePlus, Sparkles, ArrowRight, ArrowLeft, Check, ShieldCheck,
  Gauge, ListChecks, Camera, TrendingDown, TrendingUp, Info, PenLine, Star,
} from "lucide-react";

const STEPS = [
  { title: "Dados do veículo", icon: Car, panel: { kicker: "Etapa 1 de 3", heading: "Dados precisos atraem mais compradores", sub: "Use a tabela FIPE oficial para preencher em segundos — marca, modelo e ano corretos passam confiança." } },
  { title: "Opcionais & detalhes", icon: ListChecks, panel: { kicker: "Etapa 2 de 3", heading: "Destaque o que seu carro tem de melhor", sub: "Opcionais e uma boa descrição fazem seu anúncio se destacar. Capriche nos detalhes." } },
  { title: "Fotos & preço", icon: Camera, panel: { kicker: "Etapa 3 de 3", heading: "O preço certo vende mais rápido", sub: "Compare com a FIPE em tempo real e publique de graça. Você pode editar tudo quando quiser." } },
];

const EMPTY = {
  marca: "", modelo: "", ano: "", ano_fabricacao: "", versao: "", preco: "", km: "",
  cor: "", cambio: "", combustivel: "", cidade: "", estado: "", blindado: false,
  placa: "", features: [], condicoes: [], fipe_price: null, fipe_code: "", fipe_ref: "", descricao: "",
};

const PANEL_BG = "https://images.unsplash.com/photo-1606590526453-9b8ee4569a70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwY2FyJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg2NzM0Njk1fDA&ixlib=rb-4.1.0&q=85";

export function AnuncioWizard({ onSubmit, submitting }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [f, setF] = useState({ ...EMPTY });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // FIPE state
  const [mode, setMode] = useState("fipe"); // fipe | manual
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [sel, setSel] = useState({ brand: null, model: null, year: null });
  const [loading, setLoading] = useState({ brands: false, models: false, years: false, valor: false });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (mode !== "fipe" || brands.length) return;
    setLoading((l) => ({ ...l, brands: true }));
    api.get("/fipe/marcas").then(({ data }) => setBrands(data)).catch(() => toast.error("Não foi possível carregar a FIPE. Use o preenchimento manual.")).finally(() => setLoading((l) => ({ ...l, brands: false })));
  }, [mode, brands.length]);

  const pickBrand = async (opt) => {
    setSel({ brand: opt, model: null, year: null });
    setModels([]); setYears([]);
    set("marca", opt.nome); set("modelo", ""); set("ano", "");
    setLoading((l) => ({ ...l, models: true }));
    try { const { data } = await api.get(`/fipe/marcas/${opt.codigo}/modelos`); setModels(data); }
    catch { toast.error("Erro ao carregar modelos."); }
    finally { setLoading((l) => ({ ...l, models: false })); }
  };

  const pickModel = async (opt) => {
    setSel((s) => ({ ...s, model: opt, year: null }));
    setYears([]);
    set("modelo", opt.nome); set("versao", opt.nome); set("ano", "");
    setLoading((l) => ({ ...l, years: true }));
    try { const { data } = await api.get(`/fipe/marcas/${sel.brand.codigo}/modelos/${opt.codigo}/anos`); setYears(data); }
    catch { toast.error("Erro ao carregar anos."); }
    finally { setLoading((l) => ({ ...l, years: false })); }
  };

  const pickYear = async (opt) => {
    setSel((s) => ({ ...s, year: opt }));
    setLoading((l) => ({ ...l, valor: true }));
    try {
      const { data } = await api.get(`/fipe/marcas/${sel.brand.codigo}/modelos/${sel.model.codigo}/anos/${opt.codigo}`);
      const price = parseBRL(data.Valor);
      const anoModelo = data.AnoModelo && data.AnoModelo < 3000 ? data.AnoModelo : parseInt((opt.nome || "").slice(0, 4));
      setF((s) => ({
        ...s,
        ano: String(anoModelo || ""),
        ano_fabricacao: s.ano_fabricacao || String(anoModelo || ""),
        combustivel: s.combustivel || data.Combustivel || "",
        fipe_price: price, fipe_code: data.CodigoFipe || "", fipe_ref: data.MesReferencia || "",
      }));
    } catch { toast.error("Erro ao consultar o valor FIPE."); }
    finally { setLoading((l) => ({ ...l, valor: false })); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 8 - images.length;
    if (remaining <= 0) { toast.info("Você já atingiu o máximo de 8 fotos."); return; }
    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) toast.info("Você pode adicionar até 8 fotos.");
    setUploading(true);
    try {
      for (const file of toAdd) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const toggleFeature = (opt) =>
    setF((s) => ({ ...s, features: s.features.includes(opt) ? s.features.filter((x) => x !== opt) : [...s.features, opt] }));

  const toggleCondicao = (opt) =>
    setF((s) => ({ ...s, condicoes: s.condicoes.includes(opt) ? s.condicoes.filter((x) => x !== opt) : [...s.condicoes, opt] }));

  const go = (next) => { setDir(next > step ? 1 : -1); setStep(next); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const validateStep = () => {
    if (step === 0) {
      if (!f.marca || !f.modelo || !f.ano) { toast.error("Preencha marca, modelo e ano."); return false; }
    }
    if (step === 1) {
      if (!f.km) { toast.error("Informe a quilometragem."); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) go(step + 1); };

  const publish = () => {
    if (!f.preco) { toast.error("Informe o preço."); return; }
    if (images.length === 0) { toast.error("Adicione pelo menos 1 foto do carro."); return; }
    onSubmit({
      ...f,
      ano: parseInt(f.ano),
      ano_fabricacao: f.ano_fabricacao ? parseInt(f.ano_fabricacao) : parseInt(f.ano),
      preco: parseFloat(f.preco),
      km: parseInt(f.km),
      images,
    });
  };

  // price vs fipe
  const fipe = f.fipe_price;
  const priceNum = parseBRL(f.preco);
  const diff = fipe && priceNum ? ((priceNum - fipe) / fipe) * 100 : null;
  const rangeMin = fipe ? fipe * 0.85 : 0;
  const rangeMax = fipe ? fipe * 1.15 : 0;
  const markerPct = fipe && priceNum ? Math.max(0, Math.min(100, ((priceNum - rangeMin) / (rangeMax - rangeMin)) * 100)) : null;

  const variants = {
    enter: (d) => ({ x: d > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -50 : 50, opacity: 0 }),
  };

  const Panel = STEPS[step].panel;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[calc(100vh-4rem)]">
      {/* FORM SIDE */}
      <div className="lg:col-span-3 px-5 sm:px-10 lg:px-16 py-10">
        {/* progress */}
        <div className="max-w-2xl mx-auto w-full">
          <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
            {STEPS.map((s, i) => (
              <div key={i} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors duration-300 ${i < step ? "bg-obsidian" : i === step ? "bg-blaze" : "bg-border"}`} />
                <div className="flex items-center gap-1.5 mt-2">
                  <s.icon className={`w-3.5 h-3.5 ${i <= step ? "text-obsidian" : "text-muted-foreground"}`} strokeWidth={1.5} />
                  <span className={`text-[11px] font-600 hidden sm:inline ${i === step ? "text-blaze" : i < step ? "text-obsidian" : "text-muted-foreground"}`}>{s.title}</span>
                </div>
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={step} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: "easeOut" }}>
              <h1 className="font-heading font-800 text-3xl md:text-4xl tracking-tight mb-1" data-testid="wizard-step-title">{STEPS[step].title}</h1>

              {/* STEP 1 */}
              {step === 0 && (
                <div className="mt-6 space-y-5" data-testid="step-1">
                  <div className="flex items-center gap-2 mb-1">
                    <button type="button" onClick={() => setMode("fipe")} data-testid="mode-fipe"
                      className={`px-3 py-1.5 text-xs font-700 rounded-sm border transition-colors ${mode === "fipe" ? "bg-obsidian text-white border-obsidian" : "border-border hover:border-obsidian"}`}>
                      <Star className="w-3 h-3 inline mr-1" /> Buscar pela FIPE
                    </button>
                    <button type="button" onClick={() => setMode("manual")} data-testid="mode-manual"
                      className={`px-3 py-1.5 text-xs font-700 rounded-sm border transition-colors ${mode === "manual" ? "bg-obsidian text-white border-obsidian" : "border-border hover:border-obsidian"}`}>
                      <PenLine className="w-3 h-3 inline mr-1" /> Preencher manualmente
                    </button>
                  </div>

                  {mode === "fipe" ? (
                    <div className="space-y-4">
                      <div><Label className="mb-2 block">Marca *</Label>
                        <FipeCombobox testid="fipe-marca" options={brands} value={sel.brand?.codigo} display={f.marca} onSelect={pickBrand} loading={loading.brands} placeholder="Selecione a marca" searchPlaceholder="Buscar marca..." />
                      </div>
                      <div><Label className="mb-2 block">Modelo *</Label>
                        <FipeCombobox testid="fipe-modelo" options={models} value={sel.model?.codigo} display={f.modelo} onSelect={pickModel} loading={loading.models} disabled={!sel.brand} placeholder={sel.brand ? "Selecione o modelo" : "Escolha a marca primeiro"} searchPlaceholder="Buscar modelo..." />
                      </div>
                      <div><Label className="mb-2 block">Ano *</Label>
                        <FipeCombobox testid="fipe-ano" options={years} value={sel.year?.codigo} display={sel.year?.nome} onSelect={pickYear} loading={loading.years || loading.valor} disabled={!sel.model} placeholder={sel.model ? "Selecione o ano" : "Escolha o modelo primeiro"} searchPlaceholder="Buscar ano..." />
                      </div>
                      {f.fipe_price != null && (
                        <div className="flex items-center gap-3 bg-obsidian text-white rounded-sm px-4 py-3" data-testid="fipe-value-card">
                          <Star className="w-5 h-5 text-blaze shrink-0" strokeWidth={1.5} />
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.15em] text-white/60">Valor FIPE {f.fipe_ref ? `· ${f.fipe_ref}` : ""}</p>
                            <p className="font-heading font-800 text-xl">{formatBRL(f.fipe_price)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><Label className="mb-2 block">Marca *</Label>
                        <Select value={f.marca} onValueChange={(v) => v && set("marca", v)}>
                          <SelectTrigger className="h-12 rounded-sm" data-testid="manual-marca"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>{STATIC_BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="mb-2 block">Modelo *</Label><Input value={f.modelo} onChange={(e) => set("modelo", e.target.value)} placeholder="Ex: Civic EXL" className="h-12 rounded-sm" data-testid="manual-modelo" /></div>
                      <div><Label className="mb-2 block">Ano do modelo *</Label><Input value={f.ano} onChange={(e) => set("ano", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2021" className="h-12 rounded-sm" data-testid="manual-ano" /></div>
                      <div><Label className="mb-2 block">Ano de fabricação</Label><Input value={f.ano_fabricacao} onChange={(e) => set("ano_fabricacao", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2020" className="h-12 rounded-sm" data-testid="manual-ano-fab" /></div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="sm:col-span-2"><Label className="mb-2 block">Versão</Label><Input value={f.versao} onChange={(e) => set("versao", e.target.value)} placeholder="Ex: 2.0 Turbo AWD" className="h-12 rounded-sm" data-testid="input-versao" /></div>
                    <div><Label className="mb-2 block">Cor</Label>
                      <Select value={f.cor} onValueChange={(v) => v && set("cor", v)}>
                        <SelectTrigger className="h-12 rounded-sm" data-testid="input-cor"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>{CORES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="mb-2 block">Câmbio</Label>
                      <Select value={f.cambio} onValueChange={(v) => v && set("cambio", v)}>
                        <SelectTrigger className="h-12 rounded-sm" data-testid="input-cambio"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>{CAMBIOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="mb-2 block">Combustível</Label>
                      <Select value={f.combustivel} onValueChange={(v) => v && set("combustivel", v)}>
                        <SelectTrigger className="h-12 rounded-sm" data-testid="input-combustivel"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>{COMBUSTIVEIS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-3 border border-border rounded-sm px-4 h-12 cursor-pointer hover:border-obsidian transition-colors" data-testid="input-blindado-wrap">
                      <Checkbox checked={f.blindado} onCheckedChange={(v) => set("blindado", !!v)} data-testid="input-blindado" />
                      <span className="text-sm font-600 flex items-center gap-1"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Blindado</span>
                    </label>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 1 && (
                <div className="mt-6 space-y-6" data-testid="step-2">
                  <div>
                    <Label className="mb-3 block font-700">Opcionais do veículo</Label>
                    <p className="text-sm text-muted-foreground mb-3 -mt-1">Selecione os itens para chamar a atenção dos compradores.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {OPCIONAIS.map((opt) => {
                        const on = f.features.includes(opt);
                        return (
                          <button type="button" key={opt} onClick={() => toggleFeature(opt)} data-testid={`chip-${opt}`}
                            className={`flex items-center gap-2 text-left text-sm font-600 px-3 py-2.5 rounded-sm border transition-colors
                              ${on ? "border-blaze bg-blaze/5 text-obsidian" : "border-border bg-white hover:border-obsidian"}`}>
                            <span className={`w-4 h-4 rounded-[3px] border flex items-center justify-center shrink-0 ${on ? "bg-blaze border-blaze" : "border-border"}`}>
                              {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </span>
                            <span className="truncate">{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="mb-3 block font-700">Condições do veículo</Label>
                    <div className="flex flex-wrap gap-2.5">
                      {CONDICOES.map((opt) => {
                        const on = f.condicoes.includes(opt);
                        return (
                          <button type="button" key={opt} onClick={() => toggleCondicao(opt)} data-testid={`cond-${opt}`}
                            className={`inline-flex items-center gap-1.5 text-sm font-600 px-3 py-2 rounded-sm border transition-colors
                              ${on ? "border-blaze bg-blaze/5 text-obsidian" : "border-border bg-white hover:border-obsidian"}`}>
                            {on ? <Check className="w-3.5 h-3.5 text-blaze" strokeWidth={3} /> : <span className="text-muted-foreground">+</span>}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label className="mb-2 block">Quilometragem *</Label>
                      <div className="relative">
                        <Gauge className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                        <Input value={f.km} onChange={(e) => set("km", e.target.value.replace(/\D/g, ""))} placeholder="55000" className="h-12 rounded-sm pl-9" data-testid="input-km" />
                      </div>
                    </div>
                    <div><Label className="mb-2 block">Cidade</Label><Input value={f.cidade} onChange={(e) => set("cidade", e.target.value)} placeholder="São Paulo" className="h-12 rounded-sm" data-testid="input-cidade" /></div>
                    <div><Label className="mb-2 block">Estado</Label>
                      <Select value={f.estado} onValueChange={(v) => v && set("estado", v)}>
                        <SelectTrigger className="h-12 rounded-sm" data-testid="input-estado"><SelectValue placeholder="UF" /></SelectTrigger>
                        <SelectContent>{ESTADOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Descrição</Label>
                    <div className="relative">
                      <Textarea value={f.descricao} maxLength={500} onChange={(e) => set("descricao", e.target.value)}
                        placeholder="Fale um pouco mais sobre os diferenciais do seu carro. Não é obrigatório." className="rounded-sm min-h-32 pb-6" data-testid="input-descricao" />
                      <span className="absolute bottom-2 right-3 text-[11px] text-muted-foreground">{f.descricao.length}/500</span>
                    </div>
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5" data-testid="antifraud-warning">
                      <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-xs text-amber-800">
                        <b>Não inclua telefone ou dados pessoais</b> na descrição. Por segurança, todo contato é feito pelo <b>chat interno da PicCar</b>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 2 && (
                <div className="mt-6 space-y-6" data-testid="step-3">
                  <div>
                    <Label className="mb-3 block font-700">Fotos do carro * <span className="text-muted-foreground font-500">({images.length}/8)</span></Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {images.map((img, i) => (
                        <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-border group">
                          <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                          {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-700 bg-obsidian text-white px-1.5 py-0.5 rounded-sm">Capa</span>}
                          <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                            className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-obsidian text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-img-${i}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      {images.length < 8 && (
                        <label className="aspect-square rounded-sm border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-blaze transition-colors" data-testid="upload-label">
                          {uploading ? <Loader2 className="w-6 h-6 animate-spin text-blaze" /> : <ImagePlus className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />}
                          <span className="text-[11px] text-muted-foreground mt-1">Adicionar</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="upload-input" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">A primeira foto será a capa do anúncio. Até 8 fotos (.jpg, .png).</p>
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5" data-testid="photo-warning">
                      <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-xs text-amber-800">Use apenas imagens do veículo. Evite mostrar placa ou documentos com dados sensíveis nas fotos.</p>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block font-700">Preço de venda *</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading font-700 text-lg text-muted-foreground">R$</span>
                      <Input value={f.preco ? Number(f.preco).toLocaleString("pt-BR") : ""} onChange={(e) => set("preco", e.target.value.replace(/\D/g, ""))}
                        placeholder="0" className="h-14 rounded-sm pl-12 font-heading font-800 text-xl" data-testid="input-preco" />
                    </div>

                    {fipe != null && (
                      <div className="mt-4 border border-border rounded-sm p-4" data-testid="fipe-feedback">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Referência FIPE {f.fipe_ref ? `· ${f.fipe_ref}` : ""}</span>
                          {diff != null && (
                            diff <= 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs font-700 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm" data-testid="fipe-badge">
                                <TrendingDown className="w-3.5 h-3.5" /> {Math.abs(diff).toFixed(0)}% abaixo da FIPE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-700 bg-amber-100 text-amber-700 px-2 py-1 rounded-sm" data-testid="fipe-badge">
                                <TrendingUp className="w-3.5 h-3.5" /> {diff.toFixed(0)}% acima da FIPE
                              </span>
                            )
                          )}
                        </div>
                        <div className="relative h-2 rounded-full bg-gradient-to-r from-emerald-400 via-amber-300 to-red-400">
                          {markerPct != null && (
                            <div className="absolute -top-1 w-4 h-4 rounded-full bg-obsidian border-2 border-white shadow" style={{ left: `calc(${markerPct}% - 8px)` }} data-testid="fipe-marker" />
                          )}
                        </div>
                        <div className="flex justify-between mt-2 text-[11px] text-muted-foreground">
                          <span>{formatBRL(rangeMin)}</span>
                          <span className="font-700 text-obsidian">FIPE {formatBRL(fipe)}</span>
                          <span>{formatBRL(rangeMax)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="mb-2 block">Placa <span className="text-muted-foreground font-500">(informação interna, não exibida)</span></Label>
                    <Input value={f.placa} onChange={(e) => set("placa", e.target.value.toUpperCase().slice(0, 8))} placeholder="ABC1D23" className="h-12 rounded-sm uppercase max-w-xs" data-testid="input-placa" />
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Usada só para verificações de segurança. Nunca aparece no anúncio.</p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* NAV */}
          <div className="flex items-center justify-between gap-3 mt-10 pt-6 border-t border-border">
            {step > 0 ? (
              <Button type="button" variant="ghost" onClick={() => go(step - 1)} data-testid="wizard-back" className="font-600"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
            ) : <span />}
            {step < 2 ? (
              <Button type="button" onClick={next} data-testid="wizard-next" className="h-12 px-8 bg-obsidian hover:bg-blaze text-white font-700 rounded-sm">
                Continuar <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={publish} disabled={submitting} data-testid="wizard-publish" className="h-12 px-8 bg-blaze hover:bg-obsidian text-white font-700 rounded-sm">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Publicar Anúncio Grátis
              </Button>
            )}
          </div>
          {step === 2 && <p className="text-center text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Contato 100% seguro via chat · Você pode editar o anúncio quando quiser</p>}
        </div>
      </div>

      {/* DARK PANEL */}
      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden">
        <div className="sticky top-16 h-[calc(100vh-4rem)]">
          <img src={PANEL_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-obsidian/80" />
          <div className="relative h-full flex flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-sm bg-white/10 backdrop-blur flex items-center justify-center"><Car className="w-5 h-5 text-blaze" strokeWidth={2} /></span>
              <span className="font-heading font-800 text-xl">Pic<span className="text-blaze">Car</span></span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
                <p className="overline text-blaze mb-3">{Panel.kicker}</p>
                <h2 className="font-heading font-800 text-3xl leading-tight tracking-tight mb-3">{Panel.heading}</h2>
                <p className="text-white/70 text-base leading-relaxed">{Panel.sub}</p>
              </motion.div>
            </AnimatePresence>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <span className="inline-flex items-center gap-1 bg-blaze/20 border border-blaze/40 px-2.5 py-1 rounded-sm font-700 text-blaze"><Sparkles className="w-3.5 h-3.5" /> 100% Grátis</span>
              <span className="text-white/50">Sem taxas. Sem pegadinhas.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
